import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Bars } from 'react-loader-spinner';
import { Card, CustomTooltip } from '../../../../components/atoms';
import { useAppSelector } from '../../../../hooks/hooksRedux';
import { RootState } from '../../../../store';
import { useTranslation } from 'react-i18next';
import { MainCustomButton } from '../../../../components/Buttons';
import { useAuth } from '../../../../store/auth';
import { BillingService } from '../../../../services/BillingService';
import { api } from '../../../../services/api';
import { successToast, errorToast } from '../../../../utils/toasts';
import i18n from '../../../../plugins/i18n/i18n';

interface AnnualFeeConfig {
  id: string;
  description: string;
  due_date: string;
  configs: Array<{
    company_type: string;
    amount: string;
    due_date: string;
    jurisdiction: {
      id: number;
      description: string;
    };
  }>;
}

const ConfigurationAnnualDateFee = () => {
  const { button, colors } = useAppSelector((state: RootState) => state.styleGuide.data);
  const { t } = useTranslation();
  const { token } = useAuth();

  const [configs, setConfigs] = useState<AnnualFeeConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, { amount: string; due_date: string }>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [activeJurisdiction, setActiveJurisdiction] = useState<number | null>(null);

  const cacheRef = useRef<{ key: string | null; data: AnnualFeeConfig[] }>({ key: null, data: [] });
  const promiseRef = useRef<Promise<AnnualFeeConfig[] | undefined> | null>(null);

  const configsKey = useMemo(
    () => `${token}|${i18n.language.toLowerCase()}`,
    [token, i18n.language]
  );

  const loadConfigs = useCallback(
    async (force = false) => {
      if (!token) {
        cacheRef.current = { key: null, data: [] };
        setConfigs([]);
        setError(null);
        setLoading(false);
        return [];
      }

      const key = configsKey;

      if (!force) {
        if (cacheRef.current.key === key && cacheRef.current.data.length) {
          setConfigs(cacheRef.current.data);
          setError(null);
          setLoading(false);
          return cacheRef.current.data;
        }

        if (promiseRef.current) {
          return promiseRef.current;
        }
      } else {
        cacheRef.current = { key: null, data: [] };
        promiseRef.current = null;
      }

      setLoading(true);
      setError(null);

      const request = BillingService.getAnnualFeeConfigs(api, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Content-Language': i18n.language.toLowerCase()
        }
      })
        .then((data) => {
          const normalized = Array.isArray(data) ? data : [];
          cacheRef.current = { key, data: normalized };
          setConfigs(normalized);
          return normalized;
        })
        .catch((err: any) => {
          const errorMessage = err?.response?.data?.error || err?.message || t('annualFee.error');
          setError(errorMessage);
          cacheRef.current = { key, data: [] };
          setConfigs([]);
          return [];
        })
        .finally(() => {
          setLoading(false);
          promiseRef.current = null;
        });

      promiseRef.current = request;
      return request;
    },
    [configsKey, token]
  );

  // Agrupa configs por jurisdição (fora do useEffect)
  const groupedByJurisdiction: Record<number, { description: string, configs: Array<AnnualFeeConfig['configs'][0] & { due_date: string; description: string }> }> = {};
  for (const fee of configs) {
    for (const cfg of fee.configs) {
      if (!groupedByJurisdiction[cfg.jurisdiction.id]) {
        groupedByJurisdiction[cfg.jurisdiction.id] = {
          description: cfg.jurisdiction.description,
          configs: []
        };
      }
      groupedByJurisdiction[cfg.jurisdiction.id].configs.push({
        ...cfg,
        due_date: fee.due_date,
        description: fee.description
      });
    }
  }
  const jurisdictionIds = Object.keys(groupedByJurisdiction).map(Number);

  useEffect(() => {
    void loadConfigs();
  }, [loadConfigs]);

  useEffect(() => {
    if (!configs.length) {
      setEditValues({});
      return;
    }

    const initialEdit: Record<string, { amount: string; due_date: string }> = {};
    configs.forEach((fee: AnnualFeeConfig) => {
      fee.configs.forEach((cfg: AnnualFeeConfig['configs'][0]) => {
        const key = `${cfg.jurisdiction.id}_${cfg.company_type}`;
        initialEdit[key] = {
          amount: cfg.amount,
          due_date: cfg.due_date
        };
      });
    });
    setEditValues(initialEdit);
  }, [configs]);

  // Seta a jurisdição ativa para a primeira aba sempre que mudar o array de abas
  useEffect(() => {
    if (jurisdictionIds.length > 0) {
      setActiveJurisdiction(jurisdictionIds[0]);
    }
  }, [jurisdictionIds.length]);

  const handleChange = (key: string, field: 'amount' | 'due_date', value: string) => {
    setEditValues((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      },
    }));
  };

  const handleSave = async (cfg: any, edit: { amount: string; due_date: string }) => {
    const key = `${cfg.jurisdiction.id}_${cfg.company_type}`;
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      await BillingService.updateAnnualFeeConfig(
        api,
        {
          company_type: cfg.company_type,
          jurisdiction_id: cfg.jurisdiction.id,
          amount: edit.amount,
          due_date: edit.due_date,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Content-Language': i18n.language.toLowerCase(),
          },
        }
      );
      successToast(t('annualFee.saved'));
      await loadConfigs(true);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error || err?.message || t('annualFee.saveError');
      errorToast(errorMessage);
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const primaryButtonColor = button.primary || '#0C1FAA';
  const secondaryColor = colors.white;

  return (
    <Card.Root className="w-full">
      <Card.Header>
        <h3 className="text-lg font-medium">
          {t('annualFee.title')}
        </h3>
      </Card.Header>
      <Card.Content>
        {loading && (
          <div className="flex items-center justify-center py-4 text-gray-500">
            <Bars height="32" width="32" color="#4B5563" />
          </div>
        )}
        {error && <p className="text-red-600">{t('annualFee.error')}</p>}
        <div className="w-full flex flex-col">
          {/* Tabs de jurisdição */}
          <div className="flex gap-6 border-b mb-4">
            {jurisdictionIds.map(jid => (
              <button
                key={jid}
                onClick={() => setActiveJurisdiction(jid)}
                className="pb-2 font-medium"
                style={{
                  color: activeJurisdiction === jid ? colors.primary : '#4b5563',
                  borderBottom: activeJurisdiction === jid ? `2px solid ${colors.primary}` : '2px solid transparent'
                }}
              >
                {groupedByJurisdiction[jid].description}
              </button>
            ))}
          </div>

          {/* Conteúdo da aba ativa */}
          <div className="pt-4">
            {activeJurisdiction && groupedByJurisdiction[activeJurisdiction] && (
              <div className="flex flex-col gap-6">
                {groupedByJurisdiction[activeJurisdiction].configs.map((cfg) => {
                  const key = `${cfg.jurisdiction.id}_${cfg.company_type}`;
                  const edit = editValues[key] || { amount: '', due_date: '' };
                  return (
                    <div key={key} className="flex flex-col md:flex-row md:items-end gap-6 bg-white border border-blue-100 rounded-xl shadow-sm p-6 transition hover:shadow-lg">
                      <div className="flex-1 min-w-[180px]">
                        <label className="block text-sm font-semibold mb-2 text-blue-800 tracking-wide">{t('annualFee.companyType')}</label>
                        <div className="font-medium text-gray-700 bg-gray-50 rounded px-3 py-2 border border-gray-200">
                          {t(`annualFee.companyTypes.${cfg.company_type}`) || cfg.company_type}
                        </div>
                      </div>
                      <div className="flex-1 min-w-[180px]">
                        <div className="flex items-center gap-1.5 mb-2">
                          <label className="block text-sm font-semibold text-blue-800 tracking-wide">{t('annualFee.amount')}</label>
                          <CustomTooltip content={t('annualFee.tooltipAmountMin')} />
                        </div>
                        <div className="relative">
                           <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">$</span>
                           <input
                             type="number"
                             className="border border-blue-200 rounded-lg pl-8 pr-3 py-2 w-full shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-300 text-gray-800 bg-gray-50 font-medium transition"
                             value={edit.amount}
                             onChange={e => handleChange(key, 'amount', e.target.value)}
                             placeholder="0.00"
                           />
                         </div>
                       </div>
                      <div className="flex-1 min-w-[180px]">
                        <label className="block text-sm font-semibold mb-2 text-blue-800 tracking-wide">{t('annualFee.dueDate')}</label>
                        <input
                          type="date"
                          className="border border-blue-200 rounded-lg px-3 py-2 w-full shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-300 text-gray-800 bg-gray-50 font-medium transition"
                          value={edit.due_date}
                          onChange={e => handleChange(key, 'due_date', e.target.value)}
                        />
                      </div>
                      <div className="flex flex-col items-end justify-end min-w-[120px] mt-4 md:mt-0">
                        <button
                          style={{ backgroundColor: primaryButtonColor, color: secondaryColor }}
                          className="text-sm font-semibold px-6 py-2 rounded-lg shadow-md hover:shadow-xl hover:brightness-110 transition disabled:opacity-60"
                          onClick={() => handleSave(cfg, edit)}
                          disabled={saving[key]}
                        >
                          {saving[key] ? t('annualFee.saving') : t('annualFee.save')}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Card.Content>
    </Card.Root>
  );
};

export { ConfigurationAnnualDateFee };